import OctApiAdaptor from '@/components/api-adaptor/OctApiAdaptor'
import OctImage from '@/components/image/OctImage.js'

const props = {
  /** Id */
  id: {
    type: String,
    default: ''
  },
  /** Name */
  name: {
    type: String,
    default: ''
  },
  /** Email */
  email: {
    type: String,
    default: ''
  },
  /** User type */
  type: {
    type: String,
    default: 'user'
  },
  /** Indicate the user is art fair account */
  artfair: {
    type: Boolean,
    default: false
  },
  /** Display Name */
  displayName: {
    type: String,
    default: ''
  },
  /** Profile Icon */
  profileIcon: {
    type: Object,
    default: OctImage
  },
  /** Cover Image */
  coverImage: {
    type: Object,
    default: OctImage
  },
  /** Trade Name */
  tradeName: {
    type: String,
    default: ''
  },
  /** Trade Name in English */
  tradeNameEN: {
    type: String,
    default: ''
  },
  /** Position */
  position: {
    type: Array,
    default: () => []
  },
  /** User's base location */
  base: {
    type: String,
    default: ''
  },
  /** Profile headline */
  title: {
    type: String,
    default: ''
  },
  /** Profile headline in English */
  titleEN: {
    type: String,
    default: ''
  },
  /** Profile description */
  description: {
    type: String,
    default: ''
  },
  /** Profile description in English */
  descriptionEN: {
    type: String,
    default: ''
  },
  /** Birthday */
  birthday: {
    type: String,
    default: ''
  },
  /** Website */
  website: {
    type: String,
    default: '' 
  },
  /** Instagram URL */
  instagram: {
    type: String,
    default: ''
  },
  /** Twitter URL */
  twitter: {
    type: String,
    default: ''
  },
  /** Facebook URL */
  facebook: {
    type: String,
    default: ''
  },
  /** Address */
  address: {
    type: String,
    default: ''
  },
  /** Address in English */
  addressEN: {
    type: String,
    default: ''
  },
  /** Telephone number */
  tel: {
    type: String,
    default: ''
  },
  /** Opening time */
  openingTime: {
    type: String,
    default: ''
  },
  /** Closing time */
  closingTime: {
    type: String,
    default: ''
  },
  /** Holidays */
  holidays: {
    type: Array,
    default: () => []
  },
  /** Admission Fee */
  admissionFee: {
    type: [String, Number],
    default: ''
  },
  /** Tags */
  tags: {
    type: Array,
    default: () => [] 
  },
  /** Extra data */
  data: {
    type: [String,Object],
    default: ''
  },
  /** News subscription */
  subscription: {
    type: Boolean,
    default: false
  }
}

export const createProps = () => {
  return Object.keys(props).reduce((acc, cur) => {
    acc[cur] = typeof props[cur].default === 'function' ?
      props[cur].default() : props[cur].default
    return acc
  }, {})
}

const categories = [
  'Modern Design',
  'Contemporary',
  'Ceramic',
  'Street',
  'Wood',
  'Pop',
  'Stone',
  'Abstract',
  'Tableware',
  'Painting',
  'Asian',
  'Photography',
  'Japan',
  'Sculpture',
  'Furniture',
  'Prints',
  'Vessel',
  'Work on Paper',
  'Open Form',
  'Design',
  'Focus on Materials',
  'Drawing',
  'Bronze',
  'Installation',
  'Metal',
  'Film/Video',
  'Jewelry',
  'Mixed Media',
  'Performance Art',
  'Reproduction'
].map(item => {
  return {
    name: item,
    slug: encodeURIComponent(item) 
  }
})

export default {
  name: 'OctUser',
  props: props,
  data () {
    return {
      categories: categories,
      nations: [
        {
          text: 'None',
          value: '',
        }
      ],
    }
  },
  computed: {
    /** Categoris by slug */
    categoriesBySlug () {
      return this.categories.reduce((acc, cur) => {
        acc[cur.slug] = cur
        return acc
      }, {})
    },
    /** Id */
    $_octUser_id: {
      get () {
        return this.id
      },
      set (val) {
        this.$emit('update:id', val)
      }
    },
    /** Name */
    $_octUser_name: {
      get () {
        return this.name
      },
      set (val) {
        this.$emit('update:name', val)
      }
    },
    /** Email */
    $_octUser_email: {
      get () {
        return this.email
      },
      set (val) {
        this.$emit('update:email', val)
      }
    },
    /** User type */
    $_octUser_type: {
      get () {
        return this.type
      },
      set (val) {
        this.$emit('update:type', val)
      }
    },
    /** Display Name */
    $_octUser_displayName: {
      get () {
        return this.displayName
      },
      set (val) {
        // Test update
        this.$emit('update:displayName', val)

        this.$nextTick(() => {
          /**
           * Normalize displayName
           * Only accept [a-z0-9_-]
           */
          val = val.toLowerCase().replace('/ /g', '_').replace(/[^a-z0-9_-]/g, '')

          // Update actually
          this.$emit('update:displayName', val)
        })
      }
    },
    /** Profile Icon */
    $_octUser_profileIcon: {
      get () {
        return this.profileIcon
      },
      set (val) {
        this.$emit('update:profileIcon', val)
      }
    },
    /** Cover Image */
    $_octUser_coverImage: {
      get () {
        return this.coverImage
      },
      set (val) {
        this.$emit('update:coverImage', val)
      }
    },
    /** Trade Name */
    $_octUser_tradeName: {
      get () {
        return this.tradeName
      },
      set (val) {
        this.$emit('update:tradeName', val)
      }
    },
    /** Trade Name in English */
    $_octUser_tradeNameEN: {
      get () {
        return this.tradeNameEN
      },
      set (val) {
        this.$emit('update:tradeNameEN', val)
      }
    },
    /** Position */
    $_octUser_position: {
      get () {
        return this.position
      },
      set (val) {
        this.$emit('update:position', val)
      }
    },
    /** User's base location */
    $_octUser_base: {
      get () {
        return this.base
      },
      set (val) {
        this.$emit('update:base', val)
      }
    },
    /** Profile headline */
    $_octUser_title: {
      get () {
        return this.title
      },
      set (val) {
        this.$emit('update:title', val)
      }
    },
    /** Profile headline in English */
    $_octUser_titleEN: {
      get () {
        return this.titleEN
      },
      set (val) {
        this.$emit('update:titleEN', val)
      }
    },
    /** Profile description */
    $_octUser_description: {
      get () {
        return this.description
      },
      set (val) {
        this.$emit('update:description', val)
      }
    },
    /** Profile description in English */
    $_octUser_descriptionEN: {
      get () {
        return this.descriptionEN
      },
      set (val) {
        this.$emit('update:descriptionEN', val)
      }
    },
    /** Birthday */
    $_octUser_birthday: {
      get () {
        return this.birthday
      },
      set (val) {
        this.$emit('update:birthday', val)
      }
    },
    /** Website */
    $_octUser_website: {
      get () {
        return this.website
      },
      set (val) {
        this.$emit('update:website', val)
      }
    },
    /** Instagram URL */
    $_octUser_instagram: {
      get () {
        return this.instagram
      },
      set (val) {
        this.$emit('update:instagram', val)
      }
    },
    /** Twitter URL */
    $_octUser_twitter: {
      get () {
        return this.twitter
      },
      set (val) {
        this.$emit('update:twitter', val)
      }
    },
    /** Facebook URL */
    $_octUser_facebook: {
      get () {
        return this.facebook
      },
      set (val) {
        this.$emit('update:facebook', val)
      }
    },
    /** Address */
    $_octUser_address: {
      get () {
        return this.address
      },
      set (val) {
        this.$emit('update:address', val)
      }
    },
    /** Address in English */
    $_octUser_addressEN: {
      get () {
        return this.addressEN
      },
      set (val) {
        this.$emit('update:addressEN', val)
      }
    },
    /** Telephone number */
    $_octUser_tel: {
      get () {
        return this.tel
      },
      set (val) {
        this.$emit('update:tel', val)
      }
    },
    /** Opening time */
    $_octUser_openingTime: {
      get () {
        return this.openingTime
      },
      set (val) {
        this.$emit('update:openingTime', val)
      }
    },
    /** Closing time */
    $_octUser_closingTime: {
      get () {
        return this.closingTime
      },
      set (val) {
        this.$emit('update:closingTime', val)
      }
    },
    /** Holidays */
    $_octUser_holidays: {
      get () {
        return this.holidays
      },
      set (val) {
        this.$emit('update:holidays', val)
      }
    },
    /** Admission Fee */
    $_octUser_admissionFee: {
      get () {
        return this.admissionFee
      },
      set (val) {
        this.$emit('update:admissionFee', val)
      }
    },
    /** Tags */
    $_octUser_tags: {
      get () {
        return this.tags
      },
      set (val) {
        this.$emit('update:tags', val)
      }
    },
    /** Extra data */
    $_octUser_data: {
      get () {
        return this.data
      },
      set (val) {
        this.$emit('update:data', val)
      }
    },
    /** News subscription */
    $_octUser_subscription: {
      get () {
        return this.subscription
      },
      set (val) {
        this.$emit('update:subscription', val)
      }
    }
  },
  created () {
    // Fetch all nationalities
    OctApiAdaptor.getNationalities()
      .then(response => this.nations.push(
        ...response.sort((a, b) => a.text.toUpperCase() < b.text.toUpperCase() ? -1 : 1)
      ))
      .catch(() => alert('国籍が取得できませんでした。しばらく経ってからもう一度やり直してください。'))
  }
}